import { IRetailerRejection } from '@models/retailer.model';

export interface ILogDataReasonLegacy {
  desc: string | null;
  enum_value: string;
  enum_string: string;
}

export interface LogDataLegacy {
  title: string;
  desc: string;
  retailer_status_enum?: string;
  submission_date?: string;
  venture_name?: string;
  desc_link?: string;
  desc_link_label?: string;
  reason?: IRetailerRejection[];
  reasons?: ILogDataReasonLegacy[];
}

export interface LogDataDistributorLegacy extends LogDataLegacy {
  distributor_id: string;
  status_enum: string;
  status_title: string;
  link_param?: any;
}

/**
 * Base interface untuk semua log data
 * Berisi field yang PASTI ada di semua response
 */
export interface ILogRecordBase {
  title: string;
  desc: string;
  submission_date: string;
}

/**
 * Reason structure (untuk Retailer & Distributor)
 */
export interface ILogReason {
  desc: string | null;
  enum_value: string;
  enum_string: string;
}

/**
 * RETAILER LOG
 */
export interface IRetailerLogRecord extends ILogRecordBase {
  retailer_id: string | null;
  retailer_status_enum: string;
  retailer_status_title: string | null;
  owner_name: string;
  venture_name: string;
  reasons: ILogReason[] | null;
}

/**
 * DISTRIBUTOR LOG
 */
export interface IDistributorLogRecord extends ILogRecordBase {
  distributor_id: string | null;
  status_enum: string;
  status_title: string | null;
  distributor_name: string | null;
  sales_name: string | null;
  reasons: ILogReason[] | null;
}

/**
 * USER LOG
 */
export interface IUserLogRecord extends ILogRecordBase {
  // User log hanya punya base fields
  // Tidak ada field tambahan
}

/**
 * PROGRAM MARKETING LOG
 */
export interface IProgramMarketingLogRecord extends ILogRecordBase {
  promotion_id: string | null;
}

/**
 * Union type untuk semua jenis log record
 * Component akan menerima salah satu dari type ini
 */
export type ILogRecord = IRetailerLogRecord | IDistributorLogRecord | IUserLogRecord | IProgramMarketingLogRecord;

/**
 * Link configuration untuk navigation
 */
export interface ILogLink {
  url: string;
  label: string;
  queryParams?: Record<string, any>;
}

/**
 * Context yang bisa dipass ke linkGenerator
 * Berisi route params atau data lain yang dibutuhkan
 */
export interface ILogContext {
  id?: string;
  status?: string;
  tab?: string;
  [key: string]: any; // Allow additional context
}

/**
 * Configuration untuk card-log component
 */
export interface ICardLogConfig {
  // Required
  urlDataApi: string;
  emptyStateText: string;

  // Optional: Custom link generator
  // Menerima record dan optional context (route params, dll)
  linkGenerator?: (record: ILogRecord, context?: ILogContext) => ILogLink | null;

  // Optional: Custom reason formatter
  reasonFormatter?: (record: ILogRecord) => string | null;

  // Optional: Query params to exclude from API call
  excludeQueryParams?: string[];
}

// ============================================
// TYPE GUARD FUNCTIONS
// ============================================

export function isRetailerLog(record: ILogRecord): record is IRetailerLogRecord {
  return 'retailer_id' in record;
}

export function isDistributorLog(record: ILogRecord): record is IDistributorLogRecord {
  return 'distributor_id' in record;
}

export function isUserLog(record: ILogRecord): record is IUserLogRecord {
  return !('retailer_id' in record) && !('distributor_id' in record) && !('promotion_id' in record);
}

export function isProgramMarketingLog(record: ILogRecord): record is IProgramMarketingLogRecord {
  return 'promotion_id' in record;
}
